import {
  Duration,
  Locale,
  format,
  formatDistance,
  formatDuration,
  intervalToDuration,
  startOfDay,
  endOfDay,
} from 'date-fns';

function getTimeInMillis(): number {
  return new Date().getTime();
}

export function getTimeInMicros(): number {
  return getTimeInMillis() * 1000;
}

export function ofHourInMillis(hours: number): number {
  return hours * 3600000;
}

export function ofDayInMillis(days: number): number {
  return days * 86400000;
}

export function calculateDuration(fromTimeMicros: number, toTimeMicros: number): number {
  return Math.abs(toTimeMicros - fromTimeMicros);
}

export function formatMsUTCToHM(millis: number) {
  const s = Math.floor(millis / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}`;
}

export function day(timeMicros: number) {
  return format(new Date(timeMicros / 1000), 'yyyy-MM-dd');
}

export function time(timeMicros: number) {
  return format(new Date(timeMicros / 1000), 'HH:mm:ss');
}

export function timeDistance(start: Date, end: Date) {
  return formatDuration(intervalToDuration({ start, end }));
}

export function timeDistanceWithRound(locale: Locale, start: Date, end: Date) {
  return formatDistance(start, end, { locale });
}

function partsOfDurationTimeMicros(durationTimeMicros: number): Duration {
  return intervalToDuration({ start: 0, end: durationTimeMicros / 1000 });
}

export function timeDuration(duartionTimeMicros: number) {
  return formatDuration(partsOfDurationTimeMicros(duartionTimeMicros));
}

export function timeDurationShort(
  t: (key: string) => string,
  durationTimeMicros: number,
  partLimit?: number,
  withoutSeconds?: boolean,
  hoursAndMinutesOnly?: boolean
) {
  const MICROS_PER_SECOND = 1_000_000;
  const MICROS_PER_MINUTE = 60 * MICROS_PER_SECOND;
  const MICROS_PER_HOUR = 60 * MICROS_PER_MINUTE;

  let formatedTimeDuration: (string | undefined)[];

  if (hoursAndMinutesOnly) {
    const totalHours = Math.floor(durationTimeMicros / MICROS_PER_HOUR);
    const remainingMinutes = Math.floor((durationTimeMicros % MICROS_PER_HOUR) / MICROS_PER_MINUTE);

    formatedTimeDuration = [
      formatIfValidValue(t, totalHours, 'hour'),
      formatIfValidValue(t, remainingMinutes, 'minute'),
    ];
  } else {
    const duration: Duration = partsOfDurationTimeMicros(durationTimeMicros);

    formatedTimeDuration = [
      formatIfValidValue(t, duration.years, 'year'),
      formatIfValidValue(t, duration.months, 'month'),
      formatIfValidValue(t, duration.days, 'day'),
      formatIfValidValue(t, duration.hours, 'hour'),
      formatIfValidValue(t, duration.minutes, 'minute'),
    ];
    if (!withoutSeconds) formatedTimeDuration.push(formatIfValidValue(t, duration.seconds, 'second'));
  }

  while (formatedTimeDuration.length > 0 && formatedTimeDuration[0] == undefined) {
    formatedTimeDuration.shift();
  }

  if (partLimit) {
    formatedTimeDuration = formatedTimeDuration.slice(0, partLimit);
  }

  return formatedTimeDuration.join(' ');
}

function formatIfValidValue(t: (key: string) => string, value: number | undefined, unitKey: string) {
  if (value && value > 0) {
    const translationKey = 'general.dateTime.' + unitKey;
    return value.toString() + t(translationKey);
  }
}

// Get start of day in microseconds (micros -> micros)
export function getStartOfDayAsMicros(micros: number | undefined): number {
  if (micros === undefined) return 0;
  const date = startOfDay(new Date(micros / 1000));
  return date.getTime() * 1000;
}

// Get end of day in microseconds (micros -> micros)
export function getEndOfDayAsMicros(micros: number | undefined): number {
  if (micros === undefined) return 0;
  const date = endOfDay(new Date(micros / 1000));
  return date.getTime() * 1000;
}

// Add days to a microsecond timestamp
export function addDaysAsMicros(micros: number | undefined, days: number): number {
  if (micros === undefined) return 0;
  const daysInMicros = ofDayInMillis(days) * 1000;
  return micros + daysInMicros;
}
